import * as React from "react";
import { useEffect } from "react";
import { Button, Checkbox, DropdownItemProps, Form, FormGroup, Modal } from "semantic-ui-react";

import { SourceType } from "@/interfaces";

import { CustomDropDownComponent } from "@/components/CustomDropDown";
import { DateRangePicker } from "@/components/DateRangePicker";

import "./styles.scss";
import { mediaTypeOptions, reviewStatusOptions, sourceTypeOptions } from "../../../options";
import { PostSearchQuery } from "@/services/bedrock";

interface Props {
  open: boolean;
  onClose: () => void;
  query: PostSearchQuery;
  onUpdate: (query: PostSearchQuery) => void;
  keywordOptions: DropdownItemProps[];
  playerOptions?: DropdownItemProps[];
  segmentationOptions?: DropdownItemProps[];
}

const FilterModalFC: React.FC<Props> = ({
  open,
  onClose,
  query,
  onUpdate,
  keywordOptions,
  playerOptions,
  segmentationOptions,
}) => {
  const [currentQuery, setCurrentQuery] = React.useState<PostSearchQuery>({ ...query });

  useEffect(() => {
    setCurrentQuery({ ...query });
  }, [query]);

  return (
    <Modal size={"large"} open={open} className="filterModal" onClose={onClose}>
      <Modal.Header>All Filters</Modal.Header>

      <Modal.Content className="filterModalContent">
        <Form className={"allFilterSearchSetFilterMenu"}>
          <FormGroup widths="equal">
            <Form.Field>
              <label className="filterDesc">Keyword</label>
              <CustomDropDownComponent
                defaultValue={currentQuery?.keyphraseIds}
                options={keywordOptions}
                placeholder={"-"}
                multiple={true}
                onChangeHandler={data => {
                  setCurrentQuery(q => {
                    return {
                      ...q,
                      keyphraseIds: data,
                    };
                  });
                }}
              />
            </Form.Field>
            <Form.Field>
              <label className="filterDesc">Review Status</label>
              <CustomDropDownComponent
                defaultValue={currentQuery.reviewStatuses}
                options={reviewStatusOptions}
                placeholder={"-"}
                multiple={true}
                onChangeHandler={data => {
                  setCurrentQuery(q => {
                    return {
                      ...q,
                      reviewStatuses: data,
                    };
                  });
                }}
              />
            </Form.Field>
          </FormGroup>
          <FormGroup widths="equal">
            <Form.Field>
              <label className="filterDesc">Segmentations</label>
              <CustomDropDownComponent
                defaultValue={currentQuery?.segmentationIds}
                options={segmentationOptions || []}
                placeholder={"-"}
                multiple={true}
                onChangeHandler={data => {
                  setCurrentQuery(q => {
                    return {
                      ...q,
                      segmentationIds: data,
                    };
                  });
                }}
              />
            </Form.Field>
            <Form.Field>
              <label className="filterDesc">Players</label>
              {playerOptions && playerOptions?.length > 0 && (
                <CustomDropDownComponent
                  defaultValue={currentQuery?.playerIds}
                  options={playerOptions}
                  placeholder={"-"}
                  multiple={true}
                  onChangeHandler={data => {
                    setCurrentQuery(q => {
                      return {
                        ...q,
                        playerIds: data,
                      };
                    });
                  }}
                />
              )}
            </Form.Field>
          </FormGroup>

          <FormGroup widths="equal">
            <Form.Field>
              <label>Source Types</label>
              <CustomDropDownComponent
                options={sourceTypeOptions}
                placeholder={"Select source types"}
                defaultValue={currentQuery?.sourceTypes}
                onChangeHandler={data => {
                  setCurrentQuery(q => {
                    return {
                      ...q,
                      sourceTypes: data as SourceType[],
                    };
                  });
                }}
                multiple={true}
              />
            </Form.Field>

            <Form.Field>
              <label>Media Type</label>
              <CustomDropDownComponent
                options={mediaTypeOptions}
                placeholder={"Select media type"}
                defaultValue={currentQuery?.mediaTypes}
                onChangeHandler={data => {
                  setCurrentQuery(cFilter => {
                    return {
                      ...cFilter,
                      mediaTypes: data,
                    };
                  });
                }}
                multiple={true}
              />
            </Form.Field>
          </FormGroup>

          <FormGroup widths={"equal"}>
            <Form.Field>
              <label className="filterDesc">Date Range</label>
              <DateRangePicker
                startDate={currentQuery?.from}
                endDate={currentQuery?.until}
                maxDate={new Date()}
                onChangeStartDate={(date: Date | undefined) => {
                  setCurrentQuery(q => {
                    return {
                      ...q,
                      from: date,
                    };
                  });
                }}
                onChangeEndDate={(date: Date | undefined) => {
                  setCurrentQuery(q => {
                    return {
                      ...q,
                      until: date,
                    };
                  });
                }}
              />
            </Form.Field>
          </FormGroup>
          <FormGroup widths={"equal"}>
            <Form.Field>
              <Checkbox
                label={"Flagged Posts Only"}
                checked={currentQuery.keyphrasePolicies?.includes("FLAG")}
                onChange={(_, data) => {
                  setCurrentQuery(f => {
                    return {
                      ...f,
                      keyphrasePolicies: data.checked ? ["FLAG"] : [],
                    };
                  });
                }}
              />
            </Form.Field>
          </FormGroup>
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <Button
          data-elm-id={"clearFiltersBtn"}
          secondary
          onClick={() => {
            onUpdate({
              ...query,
              keyphraseIds: [],
              segmentationIds: [],
              playerIds: [],
              sourceTypes: [],
              mediaTypes: [],
              reviewStatuses: [],
              from: undefined,
              until: undefined,
              keyphrasePolicies: [],
            });
            onClose();
          }}
        >
          Clear Filters
        </Button>
        <Button
          data-elm-id={`applyFiltersBtn`}
          primary
          onClick={() => {
            onUpdate(currentQuery);
            onClose();
          }}
        >
          Apply Filters
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export const FilterModal = FilterModalFC;
